import { Getter, Setter, atom } from "jotai";
import { Editor, Box, TLShapeId } from "tldraw";
import type { SlideSummary } from "@/services/api";
import type { Spec } from "@json-render/core";
import type { WorkInfo, ChatMessagesItem, ClassOverInfo, GenerateTask, GenerateTaskInfo } from "@/services/api";
import type { AgentChatMessage } from "@/components/DrawBoard/agent-chat/types";
import type { ShapeAttachment } from "@/types/attachment";
import { asyncGetAudio } from "@/services/textToSpeech";

// 导入整合的TTS状态
// 这些atom现在从ttsAtom.ts导入，保持单一数据源
import {
  voiceDataAtom as ttsVoiceDataAtom,
  playTasksAtom as ttsPlayTasksAtom,
  currentPlayingAtom as ttsCurrentPlayingAtom,
  isVoiceAtom as ttsIsVoiceAtom,
  voiceConfigAtom as ttsVoiceConfigAtom,
  voiceRatioAtom as ttsVoiceRatioAtom,
  ttsPlayerAtom
} from "./ttsAtom";

interface ChatMessagesListOpts {
  type?: "init" | "add" | "updateLast" | "clear";
  list?: ChatMessagesItem[];
  item?: ChatMessagesItem;
}
interface DalleData {
  url: string;
  sid: string;
}
export interface SlideInfo {
  slideId: string;
  sid?: string;
  taskId?: number;
}

interface EditorData {
  action?: "create" | "modify" | "delete";
  shape?: any;
  end?: boolean;
  sid?: string;
  firstShapeId?: string;
  msgSid?: string;
  firstBlock?: boolean;
}

interface SelectionBounds {
  bounds: Box;
  shapeId: TLShapeId;
}

export interface CoderData {
  text?: string;
  status?: "add" | "loading" | "done";
  sid?: string;
}

export enum BTN_STATUS {
  DISABLED = 0, // 禁用
  WAITING = 1,
  RECORDING = 2,
  SEND = 3,
}

export const workIdAtom = atom<string>("");
export const editorAtom = atom<Editor | null>(null);
export const lessonEditorAtom = atom<Editor | null>(null);
export const showStylePanelAtom = atom<boolean>(false);
export const keepOpenStylePanelAtom = atom<boolean>(false);
export const workDataAtom = atom<WorkInfo | GenerateTaskInfo | null>(null);
export const classOverInfoAtom = atom<ClassOverInfo | null>(null);
export const workNameAtom = atom<string>("");
// 重新导出TTS相关的atom（从ttsAtom.ts导入，单一数据源）
export const voiceDataAtom = ttsVoiceDataAtom;
export const playTasksAtom = ttsPlayTasksAtom;
export const messageCompleteSidsAtom = atom<any>({});
// 重新导出TTS相关的atom（从ttsAtom.ts导入，单一数据源）
export const currentPlayingAtom = ttsCurrentPlayingAtom;
export const hasPlayedHistoryAtom = atom<boolean>(false);

// 类型守卫：判断是否为 WorkInfo
function isWorkInfo(data: WorkInfo | GenerateTaskInfo | null): data is WorkInfo {
  return data !== null && 'generateId' in data === false;
}

// 自拼和板书不支持题目和文本合并菜单形式
export const isPracticeCourseAtom = atom<boolean>(
  (get) => {
    const workData = get(workDataAtom);
    // 如果是 GenerateTaskInfo，默认返回 true（不是练习课，支持菜单合并）
    if (!isWorkInfo(workData)) {
      return true;
    }
    // 如果是 WorkInfo，检查 seriesId
    return workData.seriesId !== 4 && workData.seriesId !== 15;
  }
);

// 保存流式消息是否推送完成状态
export const isCompleteSidAtom = atom<any>({});

export enum LoadingType {
  /* 加载中 */
  Loading = "loading",
  /* 错误 */
  Error = "error",
  /* ws 完成连接 */
  Conencted = "conencted",
  /* 画板加载完成 */
  DrawBoardLoaded = "drawboardLoaded",
  /* 画板和 ws 都已完成*/
  AllReady = "ready",
}
// 0: loading 1: conected 1: success -1: error
export const loadingStatusAtom = atom<LoadingType>(LoadingType.Loading);
// 用户是否点击去上课
export const userClickedAtom = atom<boolean>(false);
// 是否准备好开始上课
export const readyStartClassAtom = atom<boolean>(
  (get) =>
    get(userClickedAtom) && get(loadingStatusAtom) === LoadingType.AllReady
);
// 重新导出TTS相关的atom（从ttsAtom.ts导入，单一数据源）
export const voiceConfigAtom = ttsVoiceConfigAtom;
// 导出整合的TTS播放器状态，供新代码使用
export { ttsPlayerAtom };

const delay = (ms?: number) => {
  return new Promise((resolve) => setTimeout(resolve, (ms || 0) * 1000));
};

const setVoiceAndPlay = async (
  get: Getter,
  set: Setter,
  messageItem: ChatMessagesItem
) => {
  await delay(messageItem.audioIndex);

  const resAudio = await asyncGetAudio(
    messageItem.message,
    get(voiceConfigAtom),
    messageItem.language,
    get(voiceRatioAtom),
    get(workDataAtom)?.seriesId
  );
  const voiceData = { ...get(voiceDataAtom) };
  const currentItem = { ...(voiceData[messageItem.sid!] || { voiceList: [] }) };
  currentItem.duration = currentItem.duration
    ? currentItem.duration + parseInt(resAudio?.addition?.duration, 10)
    : parseInt(resAudio?.addition?.duration, 10);
  if (!currentItem.voiceList) {
    currentItem.voiceList = [];
  }
  currentItem.voiceList[messageItem.audioIndex!] = {
    ...resAudio,
    ...messageItem,
  };
  set(voiceDataAtom, {
    ...voiceData,
    [messageItem.sid!]: currentItem,
  });
};

const readChatMessagesListAtom = atom<ChatMessagesItem[]>([]);
export const chatMessagesListAtom = atom(
  (get) => get(readChatMessagesListAtom),
  (get: Getter, set: Setter, opts: ChatMessagesListOpts) => {
    const isVoice = get(isVoiceAtom);
    const playTasks = [...get(playTasksAtom)];
    const list: ChatMessagesItem[] = [...get(readChatMessagesListAtom)];
    const loading = get(loadingAtom);
    if (opts.type === "init" && opts.list) {
      const tmpList = [...opts.list].reverse();
      set(readChatMessagesListAtom, list.concat(tmpList));
    } else if (opts.type === "add" && opts.item) {
      if (opts.item.name === "slide") {
        const item = list.find((item) => item.sid === opts.item?.sid);
        if (item) {
          set(
            readChatMessagesListAtom,
            list.map((itemMsg) => {
              if (itemMsg.sid === opts.item?.sid) {
                return {
                  ...itemMsg,
                  resources: opts.item?.resources,
                };
              }
              return itemMsg;
            })
          );
        } else {
          list.unshift({ ...opts.item });
          set(readChatMessagesListAtom, list);
        }
      } else {
        list.unshift(opts.item);
        set(readChatMessagesListAtom, list);
      }
    } else if (opts.type === "updateLast" && opts.item?.message) {
      // ✅ 优雅设计：让 playTasks 和 gottenAudio 自然控制 loading 状态
      // 不在这里清除 loading，避免在音频播放前就显示"点击和老师对话"
      // 只在没有音频时清除 loading
      if (!isVoice && loading && !loading.type) {
        set(loadingAtom, null);
      }
      const { message, sid, audioIndex } = opts.item;
      let newMessage = message + "\n";

      const item = list.find((item) => item.sid === sid);
      if (!item || (item && item.name === "slide")) {
        let tmpItem;
        if (item && item.name === "slide") {
          tmpItem = list.splice(0, 1)[0];
        }
        list.unshift({
          name: "teacher",
          content: newMessage + "\n",
          isVoice,
          sid,
          slideId: get(slideIdAtom),
          taskId: get(slideInfoAtom)?.taskId,
          resources: tmpItem?.resources,
        });

        if (isVoice) {
          set(voiceDataAtom, {
            ...get(voiceDataAtom),
            [sid!]: {
              voiceList: [],
              length: 1,
              slideId: get(slideIdAtom),
              taskId: get(slideInfoAtom)?.taskId,
            },
          });
          playTasks.push(sid!);
          set(playTasksAtom, playTasks);
          setVoiceAndPlay(get, set, opts.item);
        }
      } else {
        if (item) {
          item.content += newMessage;
          item.content = item.content?.replace(/\\n/g, "\n");
          item.content = item.content + "\n";

          if (isVoice && sid) {
            const data = get(voiceDataAtom)?.[sid];
            set(voiceDataAtom, {
              ...get(voiceDataAtom),
              [sid]: {
                ...data,
                length:
                  (data.length || 0) <= audioIndex! + 1
                    ? audioIndex! + 1
                    : data.length,
              },
            });
            setVoiceAndPlay(get, set, opts.item);
          }
        }
      }
      set(readChatMessagesListAtom, list);
    } else if (opts.type === "clear") {
      set(readChatMessagesListAtom, []);
    }
    return list;
  }
);

export const btnStatusAtom = atom(BTN_STATUS.DISABLED);

export const possibleResponsesAtom = atom<string[]>([]);

// 已选择 shape 位置
export const selectionBoundsAtom = atom<SelectionBounds | null>(null);
//
export const slideInfoAtom = atom<SlideInfo | null>(null);
export const slideIdAtom = atom((get) => get(slideInfoAtom)?.slideId);
// 画板是否修改
export const isUpdateDrawboardAtom = atom<boolean>(false);

// 生图 loading
export const loadingAtom = atom<{
  type?: "coder_loading" | "dalle_loading" | "editor_loading";
  status: boolean;
  slideId?: string;
} | null>(null);

// 生成图片数据
export const dalleDataAtom = atom<DalleData | null>(null);
// 生成代码数据
export const readCoderDataAtom = atom<CoderData | null>(null);
export const coderDataAtom = atom(
  (get) => get(readCoderDataAtom),
  (get: Getter, set: Setter, data: CoderData | null) => {
    const { text: prevText = "", status: prevStatus }: CoderData =
      get(coderDataAtom) || {};
    const { status, text, sid } = data || {};
    if (status === "loading") {
      set(readCoderDataAtom, {
        status: !prevStatus ? "add" : "loading",
        text: prevText + text,
        sid,
      });
    } else if (status === "done" && text) {
      set(readCoderDataAtom, {
        status: "done",
        text,
        sid,
      });
    } else {
      set(readCoderDataAtom, data);
    }
  }
);

let queueText: any[] = [];
let runningShape = false;

export const writingShapeAtom = atom<any>(null);
export const shapeTextBlockLoadingAtom = atom(false, (get, set, value) => {
  const writingShapeList = get(writingShapeListAtom);
  if (!value && !queueText.length && writingShapeList?.length > 0) {
    set(shapeCacheListAtom, {
      action: "updateLast",
      data: get(writingShapeListAtom).map((item: any) =>
        get(editorAtom)?.getShape(item)
      ),
    });
    set(writingShapeListAtom, []);
  }
  set(shapeTextBlockLoadingAtom, value);
});

const runQueue = (get: any, set: any) => {
  if (queueText.length > 0) {
    typedEditor(get, set, queueText[0]);
  }
};

const typedEditor = (get: any, set: any, data: any) => {
  set(writingShapeAtom, {
    id: data.shape?.shapeId || data.shape?.id,
    note: data.shape?.meta?.note,
  });
  let index = 1;
  const text = data.shape.props?.text;
  const tmpData = { ...data };
  const timer = setInterval(() => {
    index++;
    tmpData.shape.props.text = text.slice(0, index);
    tmpData.shape.isLocked = !(index >= text.length);
    set(editorDataAtom, { ...tmpData, textEnd: index >= text.length });

    if (index >= text.length) {
      clearInterval(timer);
      queueText.shift();
      if (queueText.length > 0) {
        runQueue(get, set); // 继续下一个
      } else {
        runningShape = false;
        set(writingShapeAtom, null);
        // 所有文字都写完了
        if (!get(shapeTextBlockLoadingAtom)) {
          setTimeout(() => {
            set(shapeCacheListAtom, {
              action: "updateLast",
              data: get(writingShapeListAtom).map((item: any) =>
                get(editorAtom).getShape(item)
              ),
            });
            set(writingShapeListAtom, []);
          }, 100);
        }
      }
    }
  }, 35);
  runningShape = true;
};

// 生成画板数据
export const editorDataAtom = atom<
  EditorData | null,
  [EditorData | null],
  void
>(null, (get, set, opts: EditorData | null) => {
  const { msgSid, ...data } = opts || {};
  const playedSidRef = get(playedSidRefAtom);
  const pendingEditorMap = { ...get(pendingEditorMapAtom) };
  if (Array.isArray(opts)) {
    set(editorDataAtom, opts);
    return;
  }
  if (!msgSid || playedSidRef[msgSid]) {
    if (
      data?.shape?.type === "text" &&
      data?.action === "create" &&
      get(slideIdAtom)
    ) {
      // 第一次创建文字块，缓存到 shapeCacheListAtom 占位
      if (data?.firstBlock) {
        set(shapeCacheListAtom, {
          data: [{...data.shape, isPlaceholder: true}],
        });
      }
      queueText.push(data);
      if (runningShape) {
        return;
      }
      runQueue(get, set);
      return;
    }
    set(editorDataAtom, opts && data ? data : null);
  } else {
    // 未播放，添加到待处理队列（支持多个）
    const list = pendingEditorMap[msgSid] || [];
    list.push(data);
    set(pendingEditorMapAtom, {
      ...pendingEditorMap,
      [msgSid]: list,
    });
  }
});
export const pendingEditorMapAtom = atom<any>({});
export const playedSidRefAtom = atom<any, [any], void>({}, (get, set, opts) => {
  const pendingEditorMap = { ...get(pendingEditorMapAtom) };
  set(playedSidRefAtom, {
    ...get(playedSidRefAtom),
    [opts?.sid!]: true,
  });
  const pendingList = pendingEditorMap[opts?.sid!];
  if (pendingList && pendingList.length) {
    // 按顺序依次触发处理
    set(editorDataAtom, pendingList);
    delete pendingEditorMap[opts?.sid!];
    set(pendingEditorMapAtom, pendingEditorMap);
  }
});

// 重新导出TTS相关的atom（从ttsAtom.ts导入，单一数据源）
export const isVoiceAtom = ttsIsVoiceAtom;

export const remainingRoundsAtom = atom<number>(0);

export const totalRoundsAtom = atom<number>(0);

export const reportDataAtom = atom<SlideSummary | null>();

export const isChatBoxOpenAtom = atom<boolean>(false);

export const isCourseSettingsOpenAtom = atom<boolean>(false);

export const isPreviewSettingsOpenAtom = atom<boolean>(false);

export const isWorkLeftPanelOpenAtom = atom<boolean>(false);

export const worksAtom = atom<any>([]);

export const teacherSelectedAtom = atom<any>(null);

export const selectedBgAtom = atom<any>(null);

export const backgroundsAtom = atom<any>(null);

export const workSelectedAtom = atom<any>(null);

export const barrageAtom = atom<any>("");

export const captionAtom = atom<any>("");

export const teachersAtom = atom<any>([]);

// 重新导出TTS相关的atom（从ttsAtom.ts导入，单一数据源）
export const voiceRatioAtom = ttsVoiceRatioAtom;

export const recentLessonAtom = atom<Array<any>>([]);

// 新增：generateTasks 列表
export const generateTasksAtom = atom<Array<GenerateTask>>([]);

// 新增：当前选中的 generateTask ID
export const currentTaskIdAtom = atom<string | null>(null);

export const chatShapesAtom = atom<Record<TLShapeId, any>>({});
export const chatTextListAtom = atom<Set<string>>(new Set([]));

export const userMessageClearingAtom = atom<boolean>(false);

export const newChatCreatingAtom = atom<boolean>(false);

export const studyTimeAtom = atom<number>(0);
export const studyStopAtom = atom<boolean>(false);

export const aiShapeTextDataAtom = atom({}, (get, set, params: any) => {
  const data: any = { ...get(aiShapeTextDataAtom) };
  const slideId = get(slideIdAtom);
  const editor = get(editorAtom);
  if (params.action === "init") {
    set(aiShapeTextDataAtom, params.data);
    return;
  }
  if (!slideId) {
    return;
  }
  if (data[slideId]) {
    if (params.firstBlock) {
      editor?.deleteShapes(
        data[slideId].map((item: any) => item.shapeId || item.id)
      );
    }
    data[slideId].push(params.data);
  } else {
    data[slideId] = [params.data];
  }
  set(aiShapeTextDataAtom, data);
});

// ai 生成的题目&文本 shape 缓存列表
export const shapeCacheListAtom = atom({}, (get, set, params: any) => {
  const data: any = { ...get(shapeCacheListAtom) };
  const slideId = get(slideIdAtom);
  if (params.action === "init") {
    set(shapeCacheListAtom, params.data);
    return;
  }
  if (!slideId) {
    return;
  }
  if (data[slideId]) {
    if (params.action === "updateLast") {
      data[slideId][data[slideId].length - 1] = params.data;
    } else {
      data[slideId].push(params.data);
    }
  } else {
    data[slideId] = [params.data];
  }
  // 长度-1 为当前选中的导航序号
  set(shapeCacheListNumAtom, data[slideId].length - 1);
  set(shapeCacheListAtom, data);
});
// 选中导航序号
export const shapeCacheListNumAtom = atom<number>(0);

export const textShapePageAtom = atom<{ page: number; slideId: string }>({
  page: 0,
  slideId: "",
});

export const writingShapeListAtom = atom<string[]>([]);

export const currentSlideQuestionsNumberAtom = atom<number>(0);

// Agent Chat Messages State
type AgentChatMessagesOpts =
  | { type: "init" | "clear"; message?: never; messages?: AgentChatMessage[] }
  | { type: "add"; message: AgentChatMessage; messages?: never }
  | { type: "update"; message: Partial<AgentChatMessage> & Pick<AgentChatMessage, "type">; messages?: never };

const readAgentChatMessagesAtom = atom<AgentChatMessage[]>([]);

export const agentChatMessagesAtom = atom(
  (get) => get(readAgentChatMessagesAtom),
  (get: Getter, set: Setter, opts: AgentChatMessagesOpts) => {
    const currentMessages = get(readAgentChatMessagesAtom);

    if (opts.type === "init" && opts.messages) {
      // 批量初始化历史消息
      set(readAgentChatMessagesAtom, opts.messages);
    } else if (opts.type === "add" && opts.message) {
      set(readAgentChatMessagesAtom, [...currentMessages, opts.message]);
    } else if (opts.type === "clear") {
      set(readAgentChatMessagesAtom, []);
    } else if (opts.type === "update" && opts.message) {
      // 优先使用 ID 精确匹配，如果没有 ID 再使用 type 匹配
      set(
        readAgentChatMessagesAtom,
        currentMessages.map((msg) => {
          // 方案1：使用 ID 精确匹配（推荐）
          if (opts.message?.id && msg.id === opts.message.id) {
            return { ...msg, ...opts.message };
          }

          // 方案2：如果没有 ID，使用 type + status 匹配（向后兼容）
          if (!opts.message?.id &&
              msg.type === opts.message.type &&
              msg.status === "loading") {
            return { ...msg, ...opts.message, id: msg.id };
          }

          return msg;
        })
      );
    }

    return get(readAgentChatMessagesAtom);
  }
);

// Agent Chat Connection State
export enum AgentChatConnectionStatus {
  DISCONNECTED = "disconnected",
  CONNECTING = "connecting",
  CONNECTED = "connected",
  ERROR = "error",
}

export const agentChatConnectionStatusAtom = atom<AgentChatConnectionStatus>(
  AgentChatConnectionStatus.DISCONNECTED
);

export const agentChatErrorAtom = atom<string | null>(null);

export const agentChatLoadingAtom = atom<boolean>(false);

// Agent Chat Input Status
export enum AgentChatInputStatus {
  DISABLED = 0,  // 禁用 - 未连接或等待 ask_human
  READY = 1,      // 就绪 - 可以发送消息
  SENDING = 2,    // 发送中
}

export const agentChatInputStatusAtom = atom<AgentChatInputStatus>(AgentChatInputStatus.DISABLED);

// Agent Chat Generation Status
export enum AgentChatGenStatus {
  IDLE = "idle",
  PLAN_GENERATING = "plan_generating",
  UNDERSTAND_GENERATING = "understand_generating",
  TODO_GENERATING = "todo_generating",
  TEACHINGTASK_GENERATING = "teachingtask_generating",
  SLIDETASK_GENERATING = "slidetask_generating",
}

export const agentChatGenStatusAtom = atom<AgentChatGenStatus>(AgentChatGenStatus.IDLE);

// Agent Chat Generated Data
export const agentChatPlanAtom = atom<string | null>(null);  // 计划文本
export const agentChatUnderstandAtom = atom<string | null>(null);  // understand 文本
export const agentChatTodoListAtom = atom<string | null>(null);  // todolist 文本（兼容旧版本）
export const agentChatTeachingTasksAtom = atom<any>(null);  // teachingTasks JSON 对象
export const currentSlideIdAtom = atom<string>("");  // 当前选中的 slide ID 用于 LessonBoard

// 新增：右侧白板侧边栏开关
export const isWhiteboardSidebarOpenAtom = atom<boolean>(false);

// 新增：上传到白板的文件
export const uploadedFileAtom = atom<File | null>(null);

// 新增：上传到白板的 PDF 文件
export const uploadedPdfFileAtom = atom<File | null>(null);

/**
 * 正在处理的文件ID（用于防止重复处理）
 * 使用文件名+大小+修改时间生成唯一ID
 */
export const processingFileAtom = atom<string | null>(null);

// 文件解析状态管理

/**
 * 文件解析加载状态 atom
 * 追踪文件是否正在解析中
 */
export const fileParseLoadingAtom = atom<boolean>(false);

/**
 * 文件解析错误状态 atom
 * 存储解析失败时的错误信息(null = 无错误)
 */
export const fileParseErrorAtom = atom<string | null>(null);

/**
 * 文件解析详细状态 atom
 * 存储解析过程的元数据,用于调试和未来功能扩展
 */
export const fileParseStateAtom = atom<{
  fileName: string | null;      // 文件名
  requestId: string | null;     // 请求ID(来自API)
  parseType: 'image' | 'pdf' | null;  // 解析类型
  timestamp: number | null;     // 解析开始时间戳
}>({
  fileName: null,
  requestId: null,
  parseType: null,
  timestamp: null,
});

// Chat attachments (选中的shape)
export const chatAttachmentsAtom = atom<ShapeAttachment[]>([]);
export const hasChatAttachmentsAtom = atom((get) => get(chatAttachmentsAtom).length > 0);

// 学习报告显示状态
export const showLearningReportAtom = atom<boolean>(false);

// 学习报告数据
export const learningReportDataAtom = atom<SlideSummary | null>(null);

// Streaming slide trees - 用于流式渲染的 Spec 数据
// 每个 slideId 对应一个 Spec，格式: { "0": Spec, "1": Spec, ... }
export const slideTreesAtom = atom<Record<string, Spec>>({});

// Slide 背景图 URL（当前项目使用的背景图）
export const slideBackgroundImageAtom = atom<string | null>(null);

// LessonSettings 面板开关状态
export const isLessonSettingsOpenAtom = atom<boolean>(false);
